import { useState, type FormEvent } from "react";

export default function Newsletter() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") || "").trim();
    const email = String(fd.get("email") || "").trim();

    setStatus("loading");
    setMessage("");

    // anti-spam honeypot: silently fake success, never submit
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功！明早 09:00 的吃瓜日报将准时送达你的邮箱。");
      form.reset();
      return;
    }
    if (!email) {
      setStatus("error");
      setMessage("请填写你的常用邮箱地址。");
      return;
    }

    const params = new URLSearchParams();
    if (String(fd.get("name") || "").trim()) params.append("name", String(fd.get("name")).trim());
    params.append("email", email);

    try {
      const res = await fetch("https://readdy.ai/api/form/daf8kjjep053ijub1l6g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const bodyLooksSpam = /spam/i.test(serverMsg);

      if (res.ok && parsed?.code === "OK" && !bodyLooksSpam) {
        setStatus("success");
        setMessage("订阅成功！明早 09:00 的吃瓜日报将准时送达你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg && !bodyLooksSpam ? serverMsg : "订阅未成功，请稍后再试或换个邮箱地址。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，订阅失败，请稍后重试。");
    }
  }

  return (
    <section id="newsletter" className="container-page py-12 md:py-16">
      <div className="overflow-hidden rounded-2xl border border-background-200 bg-gradient-to-br from-background-100 via-background-50 to-secondary-100/50 p-6 md:p-10">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-primary-600">
              <i className="ri-mail-send-fill"></i>
              DAILY NEWSLETTER
            </p>
            <h2 className="mt-3 font-display text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              订阅吃瓜日报
              <span className="text-accent-600">每日热榜</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              每天 09:00 收到一份简洁的今日热榜与必看清单，碎片时间也能高效吃瓜。退订随时可，放心订阅。
            </p>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-readdy-form="chigua-newsletter" noValidate className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1.6fr]">
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="你的称呼（选填）"
                  className="rounded-md border border-foreground-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com · 常用邮箱"
                  className="rounded-md border border-foreground-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button type="submit" disabled={status === "loading"} className="btn-primary w-full py-3 sm:w-auto">
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i> 立即订阅每日热榜
                  </>
                )}
              </button>

              {status === "success" && (
                <p className="flex items-start gap-2 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-fill mt-0.5"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="flex items-start gap-2 text-sm text-primary-700">
                  <i className="ri-error-warning-fill mt-0.5"></i>
                  {message}
                </p>
              )}

              <p className="text-xs text-foreground-500">
                订阅即表示同意接收每日热榜邮件；我们不会向第三方分享你的信息。
              </p>

              {/* honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                data-bot-field="true"
              />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}